/**
 * Client-side request queue with concurrency limits, retry with backoff, and GET deduplication.
 */

interface QueuedRequest<T = unknown> {
  path: string;
  method: string;
  execute: () => Promise<T>;
  resolve: (value: T) => void;
  reject: (reason: unknown) => void;
  retries: number;
}

const ANALYSIS_PATHS = ['/analyze-code', '/analyze-repo', '/analyses/queue'];
const MAX_RETRIES = 3;
const BASE_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 30_000;

 
export class RequestQueue {
  private generalQueue: QueuedRequest[] = [];
  private analysisQueue: QueuedRequest[] = [];
  private activeGeneral = 0;
  private activeAnalysis = 0;
  private readonly generalConcurrency: number;
  private readonly analysisConcurrency: number;
  private inflightGets = new Map<string, Promise<unknown>>();

  constructor(generalConcurrency = 6, analysisConcurrency = 1) {
    this.generalConcurrency = generalConcurrency;
    this.analysisConcurrency = analysisConcurrency;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async enqueue<T>(
    path: string,
    method: string,
    execute: () => Promise<Response>,
  ): Promise<Response> {
    // GET deduplication: if same GET is in-flight, return clone so each caller can consume body
    if (method === 'GET') {
      const key = `GET:${path}`;
      const existing = this.inflightGets.get(key);
      if (existing) {
        return (existing as Promise<Response>).then((r) => r.clone());
      }

      const promise = this._enqueue<Response>(path, method, execute);
      // Store a promise that clones on resolve so the original is never consumed by dedup callers
      this.inflightGets.set(key, promise.then((r) => r.clone()));
      promise.finally(() => this.inflightGets.delete(key));
      return promise;
    }

    return this._enqueue<Response>(path, method, execute);
  }

  private _enqueue<T>(path: string, method: string, execute: () => Promise<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const request: QueuedRequest<T> = { path, method, execute, resolve, reject, retries: 0 };
      const isAnalysis = ANALYSIS_PATHS.some((p) => path.startsWith(p));

      if (isAnalysis) {
        this.analysisQueue.push(request as QueuedRequest);
      } else {
        this.generalQueue.push(request as QueuedRequest);
      }

      this.processQueues();
    });
  }

  private processQueues(): void {
    // Process analysis queue
    while (this.analysisQueue.length > 0 && this.activeAnalysis < this.analysisConcurrency) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const request = this.analysisQueue.shift()!;
      this.activeAnalysis++;
      this.executeRequest(request, true);
    }

    // Process general queue
    while (this.generalQueue.length > 0 && this.activeGeneral < this.generalConcurrency) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const request = this.generalQueue.shift()!;
      this.activeGeneral++;
      this.executeRequest(request, false);
    }
  }

  private async executeRequest(request: QueuedRequest, isAnalysis: boolean): Promise<void> {
    try {
      const result = await request.execute();

      // Check for 429 and retry
      if (result && typeof result === 'object' && 'status' in result) {
        const response = result as Response;
        if (response.status === 429 && request.retries < MAX_RETRIES) {
          const retryAfter = response.headers.get('Retry-After');
          const backoff = retryAfter
            ? parseInt(retryAfter, 10) * 1000
            : Math.min(BASE_BACKOFF_MS * Math.pow(2, request.retries) + Math.random() * 500, MAX_BACKOFF_MS);

          request.retries++;
          await new Promise((r) => setTimeout(r, backoff));

          // Re-queue — let the finally block handle decrement + processQueues
          if (isAnalysis) {
            this.analysisQueue.unshift(request);
          } else {
            this.generalQueue.unshift(request);
          }

          return;
        }
      }

      request.resolve(result);
    } catch (err) {
      request.reject(err);
    } finally {
      if (isAnalysis) this.activeAnalysis--;
      else this.activeGeneral--;
      this.processQueues();
    }
  }
}

 
export const globalQueue = new RequestQueue();
